import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ENVIRONMENT } from 'shared';

/** Must match Appointment::RECURRENCE_RULES on the backend. */
export type RecurrenceRule = 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'yearly';

/** Rules for which the weekday selection is meaningful (Appointment::WEEKDAY_RULES). */
export const WEEKDAY_RULES: RecurrenceRule[] = ['weekly', 'biweekly'];

/**
 * Admin status of a series (Appointment#status). Priority on the backend:
 * inactive > cancelled > draft > released.
 */
export type AppointmentStatus = 'draft' | 'released' | 'cancelled' | 'inactive';

/** Admin list filter: one of the statuses, or everything. */
export type AppointmentStatusFilter = AppointmentStatus | 'all';

/** A target group as the member sees it — compact label on the appointment. */
export interface AppointmentAudienceLabel {
  key: string;
  name: string;
}

/**
 * One expanded occurrence, as the member list renders it.
 *
 * `starts_at`/`ends_at` are UTC instants — the client converts them to the
 * reader's own zone. `local_time`/`time_zone` are the same moment in the zone the
 * admin declared, and are always shown next to it: for a member abroad, "20:00
 * Europe/Vienna" is the number the organiser will say out loud.
 *
 * `title`/`details` are already resolved to the requested language (requested →
 * base language → primary language), so the client never picks a translation.
 */
export interface AppointmentOccurrence {
  id: number;
  /** Unique per occurrence, not per series — a weekly call yields several rows for one id. */
  occurrence_id: string;
  title: string;
  details: string | null;
  join_url: string | null;
  /** Primary language of the appointment (not necessarily the one `title` is in). */
  language: string;
  location?: string | null;
  starts_at: string;
  ends_at: string | null;
  duration_minutes: number | null;
  time_zone: string;
  local_time: string;
  local_end_time: string | null;
  local_date: string;
  time_zone_abbreviation: string;
  recurring: boolean;
  recurrence_rule: RecurrenceRule;
  /** The whole series or this one occurrence is cancelled. Stays listed, with a note. */
  cancelled?: boolean;
  cancellation_note?: string | null;
  /** Only admins see internal appointments. */
  internal_only?: boolean;
  /** Target groups, empty for internal appointments. */
  audience_labels?: AppointmentAudienceLabel[];
}

export interface AppointmentListResponse {
  appointments: AppointmentOccurrence[];
  window: { weeks: number; from: string; to: string };
  language: string;
}

/** Member detail (`GET /api/appointments/:id`): the series plus its next occurrences. */
export type AppointmentDetail = Omit<AppointmentOccurrence, 'occurrence_id'> & {
  next_occurrences: AppointmentOccurrence[];
};

/** Next occurrence of a series, in the appointment's own zone. */
export interface AppointmentNextOccurrence {
  /** UTC instant. */
  starts_at: string;
  local_date: string;
  local_time: string;
  time_zone: string;
}

/** A series as the admin edits it (Appointment#as_admin_json). */
export interface Appointment {
  id: number;
  /** Board the appointment was created on — origin/audit only, no filter. */
  origin_config_domain_id: number;
  /** Primary language: `title`/`details` are written in it. */
  language: string;
  title: string;
  details: string | null;
  /** Translations without the primary language, e.g. `{ en: '…', es: '…' }`. */
  title_i18n: Record<string, string>;
  details_i18n: Record<string, string>;
  join_url: string | null;
  location: string | null;
  starts_at: string;
  /** Wall-clock date/time in `time_zone` — what the admin typed, round-tripped. */
  local_date: string;
  local_time: string;
  duration_minutes: number | null;
  time_zone: string;
  recurrence_rule: RecurrenceRule;
  recurrence_weekdays: number[];
  recurrence_until: string | null;
  active: boolean;
  /** A — boards (config domain ids) whose calendar shows this appointment. */
  calendar_ids: number[];
  /** B — community role ids that see it (ignored while `internal_only`). */
  audience_role_ids: number[];
  audiences: { id: number; key: string; name: string }[];
  /** Admins only; replaces the audience selection. */
  internal_only: boolean;
  status: AppointmentStatus;
  released_at: string | null;
  released_by_email: string | null;
  updated_by_email: string | null;
  cancelled_at: string | null;
  cancellation_note: string | null;
  /** ISO dates (`YYYY-MM-DD`, appointment's zone) of single cancelled occurrences. */
  cancelled_occurrences: string[];
  /** Older appointments this (draft) replaces once released. */
  superseded_ids: number[];
  superseded_by_id: number | null;
  next_occurrence: AppointmentNextOccurrence | null;
  /** Also announced as a thread in the News channel of every selected calendar. */
  publish_to_news: boolean;
  news_threads: { config_domain_id: number; thread_id: number }[];
  created_by_id: number | null;
  created_by_email: string | null;
  created_at: string;
  updated_at: string;
}

/** One calendar (board) the admin can assign. */
export interface AppointmentCalendar {
  /** config_domain_id */
  id: number;
  slug: string;
  name: string;
  languages: string[];
}

/** One community role the admin can target. */
export interface AppointmentRole {
  id: number;
  key: string;
  name: string;
  automatic: boolean;
}

export interface AppointmentAdminListResponse {
  appointments: Appointment[];
  calendars: AppointmentCalendar[];
  roles: AppointmentRole[];
  time_zones: string[];
  languages: string[];
}

export interface AppointmentAdminFilters {
  /** config_domain_id */
  calendar?: number | null;
  /** role key */
  role?: string | null;
  status?: AppointmentStatusFilter | null;
  q?: string | null;
}

export interface AppointmentRequest {
  language: string;
  title: string;
  details?: string | null;
  title_i18n?: Record<string, string>;
  details_i18n?: Record<string, string>;
  join_url?: string | null;
  location?: string | null;
  local_date: string;
  local_time: string;
  duration_minutes?: number | null;
  time_zone: string;
  recurrence_rule: RecurrenceRule;
  recurrence_weekdays?: number[];
  recurrence_until?: string | null;
  active?: boolean;
  publish_to_news?: boolean;
  calendar_ids?: number[];
  audience_role_ids?: number[];
  internal_only?: boolean;
}

/** Inline change from the admin overview (`PATCH /:id/assignments`). */
export interface AppointmentAssignments {
  calendar_ids: number[];
  audience_role_ids: number[];
  internal_only: boolean;
}

export interface AppointmentBulkReleaseResponse {
  released: number[];
  failed: { id: number; errors: string[] }[];
}

/**
 * Appointments — the central calendar (Phase 1).
 *
 * Member endpoints are scoped by the request's board on the backend (which
 * calendars the appointment is in × which roles the member holds), so this
 * service never passes a domain. The admin endpoints are tenant-wide: one
 * appointment, assigned to any number of calendars and audiences.
 */
@Injectable({ providedIn: 'root' })
export class AppointmentService {
  private readonly apiUrl = this.environment.apiUrl || 'http://localhost:3000/api';
  private readonly memberBase = `${this.apiUrl}/appointments`;
  private readonly adminBase = `${this.apiUrl}/admin/appointments`;

  constructor(private http: HttpClient, @Inject(ENVIRONMENT) private environment: any) {}

  /**
   * Upcoming occurrences for the member list.
   *
   * `language` only selects the translation of title/details now — it no longer
   * filters which appointments exist.
   */
  getUpcoming(language: string, weeks = 4): Observable<AppointmentListResponse> {
    const params = new HttpParams().set('language', language).set('weeks', String(weeks));
    return this.http.get<AppointmentListResponse>(this.memberBase, { params });
  }

  /** One visible series with its next occurrences; 404 when the member may not see it. */
  getOne(id: number, language?: string, weeks?: number): Observable<{ appointment: AppointmentDetail }> {
    let params = new HttpParams();
    if (language) {
      params = params.set('language', language);
    }
    if (weeks) {
      params = params.set('weeks', String(weeks));
    }
    return this.http.get<{ appointment: AppointmentDetail }>(`${this.memberBase}/${id}`, { params });
  }

  getAll(filters: AppointmentAdminFilters = {}): Observable<AppointmentAdminListResponse> {
    let params = new HttpParams();
    if (filters.calendar !== null && filters.calendar !== undefined) {
      params = params.set('calendar', String(filters.calendar));
    }
    if (filters.role) {
      params = params.set('role', filters.role);
    }
    if (filters.status) {
      params = params.set('status', filters.status);
    }
    if (filters.q && filters.q.trim()) {
      params = params.set('q', filters.q.trim());
    }
    return this.http.get<AppointmentAdminListResponse>(this.adminBase, { params });
  }

  getAdmin(id: number): Observable<{ appointment: Appointment }> {
    return this.http.get<{ appointment: Appointment }>(`${this.adminBase}/${id}`);
  }

  /** Always creates a draft — releasing is its own step. */
  create(appointment: AppointmentRequest): Observable<{ appointment: Appointment }> {
    return this.http.post<{ appointment: Appointment }>(this.adminBase, { appointment });
  }

  /** Leaves the status unchanged. */
  update(id: number, appointment: AppointmentRequest): Observable<{ appointment: Appointment }> {
    return this.http.patch<{ appointment: Appointment }>(`${this.adminBase}/${id}`, { appointment });
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.adminBase}/${id}`);
  }

  /** Calendars (A) and audiences (B) — takes effect at once, also after release. */
  patchAssignments(id: number, assignments: AppointmentAssignments): Observable<{ appointment: Appointment }> {
    return this.http.patch<{ appointment: Appointment }>(`${this.adminBase}/${id}/assignments`, assignments);
  }

  release(id: number): Observable<{ appointment: Appointment }> {
    return this.http.post<{ appointment: Appointment }>(`${this.adminBase}/${id}/release`, {});
  }

  releaseMany(ids: number[]): Observable<AppointmentBulkReleaseResponse> {
    return this.http.post<AppointmentBulkReleaseResponse>(`${this.adminBase}/release`, { ids });
  }

  /** Cancels the whole series, or one occurrence when `occurrence_date` (YYYY-MM-DD, appointment zone) is given. */
  cancel(id: number, body: { note: string; occurrence_date?: string | null }): Observable<{ appointment: Appointment }> {
    const payload: Record<string, string> = { note: body.note };
    if (body.occurrence_date) {
      payload['occurrence_date'] = body.occurrence_date;
    }
    return this.http.post<{ appointment: Appointment }>(`${this.adminBase}/${id}/cancel`, payload);
  }

  uncancel(id: number, body: { occurrence_date?: string | null } = {}): Observable<{ appointment: Appointment }> {
    const payload: Record<string, string> = {};
    if (body.occurrence_date) {
      payload['occurrence_date'] = body.occurrence_date;
    }
    return this.http.post<{ appointment: Appointment }>(`${this.adminBase}/${id}/uncancel`, payload);
  }
}
